import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Simulates a human-like typing effect (word chunks + variable delays).
 * Ported from ann-web-app `useTypewriterEffect`.
 *
 * @param {string} fullText
 * @param {boolean} isActive — When true, progressively reveals `fullText`; when false, shows full text once complete.
 * @param {object} [options]
 * @returns {{ displayedText: string, isTyping: boolean }}
 */
export const useTypewriterEffect = (fullText, isActive, options = {}) => {
  const {
    minWords = 2,
    maxWords = 5,
    minDelay = 30,
    maxDelay = 80,
    punctuationDelay = 180,
    commaDelay = 100,
    initialSlowdown = 2,
    slowdownChunks = 3,
  } = options;

  const [displayedText, setDisplayedText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const timeoutRef = useRef(null);
  const currentIndexRef = useRef(0);
  const chunkCountRef = useRef(0);
  const prevTextRef = useRef('');
  const isActiveRef = useRef(isActive);

  useEffect(() => {
    isActiveRef.current = isActive;
  }, [isActive]);

  const getRandomInt = useCallback((min, max) => {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }, []);

  const getPunctuationDelay = useCallback(
    (chunk) => {
      if (!chunk) return 0;
      const lastChar = chunk.trim().slice(-1);
      if (['.', '?', '!'].includes(lastChar)) return punctuationDelay;
      if ([',', ';', ':'].includes(lastChar)) return commaDelay;
      return 0;
    },
    [punctuationDelay, commaDelay],
  );

  useEffect(() => {
    if (!fullText) {
      setDisplayedText('');
      setIsTyping(false);
      return;
    }

    const words = fullText.split(' ');
    const prevText = prevTextRef.current || '';
    const currentWordIndex = currentIndexRef.current;

    if (!isActive && currentWordIndex === 0 && !prevText) {
      setDisplayedText(fullText);
      prevTextRef.current = fullText;
      currentIndexRef.current = words.length;
      setIsTyping(false);
      return;
    }

    prevTextRef.current = fullText;

    if (currentWordIndex > words.length) {
      currentIndexRef.current = words.length;
    }

    if (currentIndexRef.current >= words.length) {
      setDisplayedText(fullText);
      if (!isActive) {
        setIsTyping(false);
      }
      return;
    }

    setIsTyping(true);

    const typeNextChunk = () => {
      const currentIndex = currentIndexRef.current;

      if (currentIndex >= words.length) {
        setDisplayedText(fullText);
        if (!isActiveRef.current) {
          setIsTyping(false);
        }
        return;
      }

      const chunkSize = getRandomInt(minWords, maxWords);
      const endIndex = Math.min(currentIndex + chunkSize, words.length);
      const newChunk = words.slice(currentIndex, endIndex).join(' ');

      currentIndexRef.current = endIndex;
      chunkCountRef.current += 1;

      const revealed = words.slice(0, endIndex).join(' ');
      setDisplayedText(revealed);

      let delay = getRandomInt(minDelay, maxDelay);

      if (chunkCountRef.current <= slowdownChunks) {
        const slowFactor =
          initialSlowdown - ((chunkCountRef.current - 1) * (initialSlowdown - 1)) / slowdownChunks;
        delay = Math.round(delay * slowFactor);
      }

      delay += getPunctuationDelay(newChunk);

      if (endIndex < words.length) {
        timeoutRef.current = setTimeout(typeNextChunk, delay);
      } else {
        setDisplayedText(fullText);
        if (!isActiveRef.current) {
          setIsTyping(false);
        }
      }
    };

    if (currentIndexRef.current < words.length) {
      timeoutRef.current = setTimeout(typeNextChunk, getRandomInt(minDelay, maxDelay));
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [
    fullText,
    isActive,
    minWords,
    maxWords,
    minDelay,
    maxDelay,
    initialSlowdown,
    slowdownChunks,
    getRandomInt,
    getPunctuationDelay,
  ]);

  const finalDisplayedText =
    !isActive && displayedText === '' && fullText ? fullText : displayedText;

  return { displayedText: finalDisplayedText, isTyping };
};
